import { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import {
  Breadcrumb,
  createSearchState,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  DensityToggle,
  explodeCSVAndGetObjectsFromLookup,
  FormSearchResultAdvanced,
  generateLocaleId,
  useDataProvider,
  useLocalization,
  useMasterData,
  useStore,
} from '@ui-framework/index';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  FormFields,
  PageSection,
  getDefaultValueByControl,
} from '@product-framework/index';
import {
  claimNumber,
  claimStatus,
  serviceDate,
  serviceJobNumber,
  serviceProviderName,
  servicePerformed,
  totalApproved,
  customerName,
  customerAddress,
  productLine,
  brand,
  customerComplaint,
  lastUpdatedDate,
  appointmentDate,
  claimStatusDate,
  approvedDateTime,
} from './accessorFunctions';

import listSchema from './schemas/list.json';
import searchSchema from './schemas/search.json';
import validationSchema from './validations';

function ListHeaderContent({ setTableDensity }) {
  return (
    <DensityToggle
      sx={{ display: 'none' }}
      onDensityChange={args => setTableDensity(args)}
    />
  );
}

function ServiceJobClaimList() {
  const defaultListTitle = {
    id: 'CLAIM_LIST_HEADING',
    defaultMessage: 'Claims',
  };
  const { formatMessage } = useLocalization();
  const masterData = useMasterData();
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);
  const [resultsFound, setResultsFound] = useState(0);
  const [tableDensity, setTableDensity] = useState('comfortable');
  const store = useStore();
  const [searchUrlParams] = useSearchParams();
  const searchTitle = searchUrlParams.get('searchTitle') || '';
  const showApprovedDateColumn =
    searchTitle === 'Approved Claims' ||
    searchTitle.startsWith('Approved Claims');
  const listSchemaForList = useMemo(
    () =>
      showApprovedDateColumn
        ? listSchema
        : listSchema.filter(col => col.name !== 'approvedDateTime'),
    [showApprovedDateColumn],
  );

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);

  const listApiUrl = `${dataProvider.getApiUrl()}/sxcl/v1/claims`;

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD_BREADCRUMB',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'SERVICEJOBS_LIST_BREADCRUMB',
        defaultMessage: 'List Claims',
      }),
      icon: IconLibrary.Claims,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: !showSearch
        ? formatMessage({
            id: 'SHOW_SEARCH_STACKABLE_BUTTON',
            defaultMessage: 'Show Search',
          })
        : formatMessage({
            id: 'HIDE_SEARCH_STACKABLE_BUTTON',
            defaultMessage: 'Hide Search',
          }),
      Icon: !showSearch ? IconLibrary.Search : IconLibrary.HideSearch,
      variant: 'contained',
      permissionName: 'SEARCH_CLAIM',
      onClick: () => {
        setShowSearch(!showSearch);
      },
    },
  ];

  const preProcessSearchRequestData = data => {
    const { id: idArg, ...other } = data;
    return Object.fromEntries(
      Object.entries({
        ...other,
      }).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );
  };

  const submitSearchForm = async data => {
    const requestData = preProcessSearchRequestData(data);
    const searchState = createSearchState(requestData, searchSchema);
    store.domain.list.set('ServiceJobClaimList', searchState);
  };

  const preProcessSearchValues = searchedParams => {
    return searchedParams;
  };

  const listProps = {
    columnPrimaryId: 'id',
    storeName: 'ServiceJobClaimList',
    dataProvider,
    schema: listSchemaForList,
    initialState: {
      showColumnFilters: false,
    },
    downloadApiUrl: null,
    listApiUrl,
    accessorFunctions: {
      claimNumber,
      claimStatus,
      serviceDate,
      serviceJobNumber,
      serviceProviderName,
      servicePerformed,
      totalApproved,
      customerName,
      customerAddress,
      productLine,
      brand,
      customerComplaint,
      lastUpdatedDate,
      appointmentDate,
      claimStatusDate,
      approvedDateTime,
    },
    onRowCountChange: rowCount => {
      setResultsFound(rowCount);
    },
    sortOrder: 'DESC',
    sortBy: 'lastModifiedDateTime',
  };

  const searchProps = {
    customComponents: FormFields,
    storeName: 'ServiceJobClaimList',
    IconLibrary,
    schema: searchSchema,
    onSubmit: submitSearchForm,
    onClear: () => {
      store.domain.list.reset('ServiceJobClaimList');
    },
    validationSchema: validationSchema(formatMessage),
    hideSearchForm: () => {
      setShowSearch(false);
    },
    tableDensity,
    initialValues: {},
    getValueByControl: getDefaultValueByControl,
  };

  const searchValueFormatter = ({ name: searchName, value }) => {
    switch (searchName) {
      case 'claimStatus': {
        const statuses = explodeCSVAndGetObjectsFromLookup(
          value,
          masterData.claimStatuses,
        );
        return statuses.map(s => s.label).join(', ');
      }
      default:
        return null;
    }
  };

  const searchParamsProps = {
    preProcessSearchValues,
    storeName: 'ServiceJobClaimList',
    schema: searchSchema,
    resultsFound,
    valueFormatter: searchValueFormatter,
  };

  const getPageTitle = () => {
    const currentStore = store.domain.list.get('ServiceJobClaimList');
    if (searchTitle && currentStore?.filters?.length > 0) {
      return {
        id: generateLocaleId(searchTitle),
        defaultMessage: searchTitle,
      };
    }
    return defaultListTitle;
  };

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <Grid2 xs={12}>
        <FormSearchResultAdvanced {...searchParamsProps} />
      </Grid2>

      {showSearch && (
        <PageSection
          containerProps={{ sx: { mb: 3 } }}
          label={formatMessage({
            id: 'CLAIM_SEARCH_HEADING',
            defaultMessage: 'Search Claims',
          })}>
          <Box>
            <CRUDComponent
              type={CRUD_COMPONENT_TYPE.SEARCH_ADVANCED}
              searchProps={searchProps}
            />
          </Box>
        </PageSection>
      )}

      <PageSection
        HeaderContent={<ListHeaderContent setTableDensity={setTableDensity} />}
        label={formatMessage(getPageTitle())}>
        <Box>
          <CRUDComponent
            type={CRUD_COMPONENT_TYPE.ADVANCED_LIST}
            listProps={listProps}
          />
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(ServiceJobClaimList);
